import axiosInstance from "@/axiosConfig";
import type {
  WidgetType,
  WidgetTypesListResponse,
  WidgetTypeResponse,
  WidgetCategory,
} from "@/shared/types/widgetType";
import { getApiUrl } from "@/shared/utils/api";

const BASE_URL = "/widget_types";

export type WidgetTypesListParams = {
  search?: string
  category?: WidgetCategory | null
  active?: boolean | null
  /** Only widget types whose feature is enabled for the org's employee scope (server-filtered). */
  availableOnly?: boolean
  page?: number
  limit?: number
};

const buildQueryParams = (params: WidgetTypesListParams = {}) => {
  const query: Record<string, string> = {};

  if (params.page) {
    query.page = params.page.toString();
  }

  if (params.limit) {
    query.limit = params.limit.toString();
  }

  if (params.search?.trim()) {
    query["q[name_cont]"] = params.search.trim();
  }

  if (params.category) {
    query["q[category_eq]"] = params.category;
  }

  if (typeof params.active === "boolean") {
    query["q[active_eq]"] = String(params.active);
  }

  if (params.availableOnly) {
    query.available_only = "true";
  }

  return query;
};

export type WidgetTypeCreatePayload = {
  widget_type: {
    code: string
    name: string
    icon: string
    category: WidgetCategory
    description?: string | null
    default_width?: number
    default_height?: number
    active?: boolean
    position?: number
  }
};

export type WidgetTypeUpdatePayload = {
  widget_type: {
    code?: string
    name?: string
    icon?: string
    category?: WidgetCategory
    description?: string | null
    default_width?: number
    default_height?: number
    active?: boolean
    position?: number
  }
};

export const widgetTypesService = {
  /**
   * List all widget types with optional filtering
   */
  list: async (
    params?: WidgetTypesListParams,
  ): Promise<WidgetTypesListResponse> => {
    const response = await axiosInstance.get<WidgetTypesListResponse>(
      getApiUrl(BASE_URL),
      { params: buildQueryParams(params) },
    );

    return response.data;
  },

  /**
   * Get a single widget type by ID
   */
  get: async (id: string): Promise<WidgetTypeResponse> => {
    const response = await axiosInstance.get<WidgetTypeResponse>(
      getApiUrl(`${BASE_URL}/${id}`),
    );

    return response.data;
  },

  /**
   * Create a new widget type
   */
  create: async (
    payload: WidgetTypeCreatePayload,
  ): Promise<{ widget_type: WidgetType }> => {
    const response = await axiosInstance.post<{ widget_type: WidgetType }>(
      getApiUrl(BASE_URL),
      payload,
    );

    return response.data;
  },

  /**
   * Update an existing widget type
   */
  update: async (
    id: string,
    payload: WidgetTypeUpdatePayload,
  ): Promise<{ widget_type: WidgetType }> => {
    const response = await axiosInstance.put<{ widget_type: WidgetType }>(
      getApiUrl(`${BASE_URL}/${id}`),
      payload,
    );

    return response.data;
  },

  /**
   * Delete a widget type
   */
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(getApiUrl(`${BASE_URL}/${id}`));
  },
};

export default widgetTypesService;
